import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Head from 'next/head';
import Link from 'next/link';
import { useContext, useEffect } from 'react';

import GiveMeAHand from '@/contents/CampSite/other/GetMeAHand';
import constMeta from '@/contents/constants/constMeta';
import Icon from '@/contents/General/Icon';
import OutLineButton from '@/contents/General/OutLineButton';
import { Board, Thumbnail } from '@/contents/General/Panels';
import Stack from '@/contents/General/Stack';
import Typography from '@/contents/General/Typography';
import { GlobalContext } from '@/contents/hooks/state';
import Layout from '@/layouts';
import OptimizedImage from '@/modules/General/OptimizedImage';
import theme from '@/styles/theme';
import type { NextPageWithLayout } from '@/types';
import { Page } from '@/types/domain';

const Container = styled(Box)`
  position: relative;
  padding: 0 ${theme.spacing(3)};
`;

const Index: NextPageWithLayout = () => {
  const { setPage } = useContext(GlobalContext);

  useEffect(() => {
    setPage(Page.ABOUT);
  }, []);

  return (
    <>
      <Head>
        <title>{constMeta.aboutSeeds.title}</title>
        <meta
          name="description"
          content={constMeta.aboutSeeds.mata_description}
        />
        <meta property="og:title" content={constMeta.aboutSeeds.title} />
        <meta
          property="og:description"
          content={constMeta.aboutSeeds.mata_description}
        />
        <meta
          property="og:image"
          content="https://iihi.life/assets/images/common/ogp.jpg"
        />
      </Head>
      <Container>
        <Box paddingTop={5} paddingBottom={7}>
          <Typography
            fontSize={28}
            fontWeight={700}
            paddingBottom={2}
            lineHeight={1.4}
            variant="h1"
          >
            シーズとは？
          </Typography>
          <Typography fontSize={15} paddingBottom={4}>
            シーズ（種）とは、iihiラッキーバザールで使用できるポイントです。キャンプ場情報を共有したり、キャンプライフを記録したり、iihiを使うと集まります。
            <br />
            <br />
            天然の森は、動物たちが集めたクルミやドングリから芽が出て木となり育っています。シーズを集めて、iihiラッキーバザールでくじを引いて、森を育てるようにキャンプライフを味わいませんか？
          </Typography>
          <Box paddingBottom={5}>
            <Thumbnail
              image_height={50}
              max_height={256}
              sx={{ '& img': { objectFit: 'contain' } }}
            >
              <OptimizedImage
                src="/assets/images/luckybazaar/about_1.png"
                width="100%"
                height="100%"
                alt="シーズについて"
              />
            </Thumbnail>
          </Box>
          <Typography
            fontSize={21}
            fontWeight={700}
            lineHeight={1.4}
            paddingBottom={3}
            variant="h2"
          >
            シーズの集め方
          </Typography>
          <Typography fontSize={15} paddingBottom={3} lineHeight={1.8}>
            シーズの集め方は、主に3つ。
            <br />
            行ったことのあるキャンプ場の情報・写真の共有、レビュー投稿、そしてお気に入りや行ってみたいキャンプ場のリストづくり。
            <br />
            <br />
            使い込まれたギアのように、みんなでiihiを育てるたび、キャンプをもっと楽しくするシーズが集まります。
          </Typography>
          <Stack
            column
            space={4}
            sx={{
              mb: 6,
              py: 4,
              px: 4.25,
              background: '#ffffff',
              borderRadius: 2,
            }}
          >
            <Stack column space={2}>
              <Thumbnail
                image_height={50}
                max_height={256}
                sx={{ '& img': { objectFit: 'contain' } }}
              >
                <OptimizedImage
                  src="/assets/images/luckybazaar/about_2.png"
                  width="100%"
                  height="100%"
                  alt="情報更新・写真登録で、キャンプ場を応援"
                />
              </Thumbnail>
              <Box>
                <Typography
                  fontSize={14}
                  fontWeight={500}
                  paddingBottom={1}
                  variant="h3"
                >
                  情報更新・写真登録で、キャンプ場を応援！
                </Typography>
                <Typography fontSize={12}>
                  行ってみたらWebの情報と違った！ という時、ありますよね。
                  <br />
                  運営に忙しいキャンプ場オーナーさんに代わって、キャンプ場の情報を更新したり、現地で撮った写真を登録すると、お礼にシーズが届きます。
                </Typography>
              </Box>
            </Stack>

            <Stack column space={2}>
              <Thumbnail
                image_height={50}
                max_height={256}
                sx={{ '& img': { objectFit: 'contain' } }}
              >
                <OptimizedImage
                  src="/assets/images/luckybazaar/about_3.png"
                  width="100%"
                  height="100%"
                  alt="キャンプ場での経験・思い出を共有"
                />
              </Thumbnail>
              <Box>
                <Typography
                  fontSize={14}
                  fontWeight={500}
                  paddingBottom={1}
                  variant="h3"
                >
                  キャンプ場での経験・思い出を共有
                </Typography>
                <Typography fontSize={12}>
                  野生の動物を見かけた！ お気に入りの区画ができた！
                  といった、キャンプ場での経験や感想を「レビュー」に投稿して、これから行く人に共有するとシーズが集まります。
                </Typography>
              </Box>
            </Stack>
            <Stack column space={2}>
              <Thumbnail
                image_height={50}
                max_height={256}
                sx={{ '& img': { objectFit: 'contain' } }}
              >
                <OptimizedImage
                  src="/assets/images/luckybazaar/about_4.png"
                  width="100%"
                  height="100%"
                  alt="お気に入りと行った！保存でも貯まる"
                />
              </Thumbnail>
              <Box>
                <Typography
                  fontSize={14}
                  fontWeight={500}
                  paddingBottom={1}
                  variant="h3"
                >
                  リストをつくってキャンプの準備を
                </Typography>
                <Typography fontSize={12}>
                  次はどのキャンプ場に行こう？
                  そんな時、気になるキャンプ場をリストアップしたり、お気に入りを保存して見返してみませんか？
                  「行った！」のボタンをONにするだけでも、シーズが集まります。
                </Typography>
              </Box>
            </Stack>
          </Stack>
          <Typography
            fontSize={21}
            fontWeight={700}
            paddingBottom={3}
            variant="h2"
          >
            シーズ獲得条件
          </Typography>
          <Box paddingBottom={4}>
            <Board no_border="true">
              <Stack alignCenter between>
                <Typography fontSize={15} fontWeight={500}>
                  新規会員登録（初回のみ）
                </Typography>
                <Typography fontSize={14} fontWeight={500} color="green">
                  + 100
                </Typography>
              </Stack>
            </Board>
          </Box>
          <Typography
            fontSize={16}
            fontWeight={700}
            paddingBottom={2}
            variant="h3"
          >
            月1限定シーズ
          </Typography>
          <Box paddingBottom={2}>
            <Board no_border="true">
              <Stack column space={1}>
                <Stack alignCenter between>
                  <Typography fontSize={14} fontWeight={500}>
                    レビュー投稿
                  </Typography>
                  <Typography fontSize={14} fontWeight={500} color="green">
                    + 100
                  </Typography>
                </Stack>
                <Stack alignCenter between>
                  <Typography fontSize={14} fontWeight={500}>
                    お気に入り追加
                  </Typography>
                  <Typography fontSize={14} fontWeight={500} color="green">
                    + 20
                  </Typography>
                </Stack>
                <Stack alignCenter between>
                  <Typography fontSize={14} fontWeight={500}>
                    行った！保存
                  </Typography>
                  <Typography fontSize={14} fontWeight={500} color="green">
                    + 20
                  </Typography>
                </Stack>
                <Stack alignCenter between>
                  <Typography fontSize={14} fontWeight={500}>
                    キャンプ場の情報編集
                  </Typography>
                  <Typography fontSize={14} fontWeight={500} color="green">
                    + 30
                  </Typography>
                </Stack>
                <Stack alignCenter between>
                  <Typography fontSize={14} fontWeight={500}>
                    サイト・宿泊施設の写真登録
                  </Typography>
                  <Typography fontSize={14} fontWeight={500} color="green">
                    + 50
                  </Typography>
                </Stack>
                <Stack alignCenter between>
                  <Typography fontSize={14} fontWeight={500}>
                    場内施設の写真登録
                  </Typography>
                  <Typography fontSize={14} fontWeight={500} color="green">
                    + 50
                  </Typography>
                </Stack>
                <Stack alignCenter between>
                  <Typography fontSize={14} fontWeight={500}>
                    景色の写真登録
                  </Typography>
                  <Typography fontSize={14} fontWeight={500} color="green">
                    + 50
                  </Typography>
                </Stack>
              </Stack>
            </Board>
          </Box>
          <Typography fontSize={14} color="grey" paddingBottom={4}>
            ※月1とは、毎月1日から月末までの1ヶ月間を指します。登録日からの日数ではないためご注意ください。
          </Typography>
          <Typography
            fontSize={16}
            fontWeight={700}
            paddingBottom={2}
            variant="h3"
          >
            毎日集まるシーズ
          </Typography>
          <Box paddingBottom={2}>
            <Board no_border="true">
              <Stack column space={1}>
                <Stack alignCenter between>
                  <Typography fontSize={14} fontWeight={500}>
                    レビュー投稿（1回 / 日）
                  </Typography>
                  <Typography fontSize={14} fontWeight={500} color="green">
                    + 10
                  </Typography>
                </Stack>
                <Stack alignCenter between>
                  <Typography fontSize={14} fontWeight={500}>
                    お気に入り追加（3回 / 日）
                  </Typography>
                  <Typography fontSize={14} fontWeight={500} color="green">
                    + 3
                  </Typography>
                </Stack>
                <Stack alignCenter between>
                  <Typography fontSize={14} fontWeight={500}>
                    行った！保存（1回 / 日）
                  </Typography>
                  <Typography fontSize={14} fontWeight={500} color="green">
                    + 3
                  </Typography>
                </Stack>
                <Stack alignCenter between>
                  <Typography fontSize={14} fontWeight={500}>
                    キャンプ場の情報編集（1回 / 日）
                  </Typography>
                  <Typography fontSize={14} fontWeight={500} color="green">
                    + 5
                  </Typography>
                </Stack>
                <Stack alignCenter between>
                  <Typography fontSize={14} fontWeight={500}>
                    キャンプ場の写真登録（1回 / 日）
                  </Typography>
                  <Typography fontSize={14} fontWeight={500} color="green">
                    + 10
                  </Typography>
                </Stack>
              </Stack>
            </Board>
          </Box>
          <Typography fontSize={14} color="grey" paddingBottom={6}>
            ※毎日集まるシーズは、毎日0時00分にリセットされ、日付が変わると再び獲得できるようになります。
          </Typography>

          <Link href="/luckybazaar">
            <a>
              <OutLineButton
                fontweight={500}
                text="ロトシーズに挑戦する"
                icon={<Icon iconType="arrow-right" />}
              ></OutLineButton>
            </a>
          </Link>
        </Box>
      </Container>
      <GiveMeAHand />
    </>
  );
};

Index.getLayout = (page) => <Layout>{page}</Layout>;

export default Index;
